import React, { useState, useEffect } from 'react';
import { useData } from '../../hooks/useData';
import { useToast } from '../../hooks/useToast';
import { studyProtocolReviewServices } from '../../APIServices/studyProtocolReviewServices';
import { reviewChecklistMetaServices } from '../../APIServices/reviewChecklistMetaServices';

function ProtocolReviewModule() {
  const { state, audit, currentUser } = useData();
  const toast = useToast();
  
  const [activeTab, setActiveTab] = useState('upload');
  const [projectId, setProjectId] = useState(null);
  const [protocol, setProtocol] = useState(null);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [extractedText, setExtractedText] = useState('');
  const [mappedSections, setMappedSections] = useState({});
  const [fileName, setFileName] = useState('');
  const [responses, setResponses] = useState({});
  const [score, setScore] = useState({ applicable: 0, reviewed: 0, pct: 0, openCrit: 0, openMajor: 0, conclusion: 'Pending' });
  const [protocolChecklistMeta, setProtocolChecklistMeta] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [checklistFilter, setChecklistFilter] = useState({
    severity: 'all',
    compliance: 'all',
    status: 'all',
    search: ''
  });

  const tabs = [
    { id: 'upload', label: 'Protocol Upload', icon: 'ti-upload' },
    { id: 'checklist', label: 'Protocol Checklist', icon: 'ti-checklist' },
    { id: 'report', label: 'Protocol Report', icon: 'ti-file-text' }
  ];

  useEffect(() => {
    setClients(state.clients || []);
    setProjects(state.projects || []);
  }, [state]);

  useEffect(() => {
    const loadChecklistMeta = async () => {
      const data = await reviewChecklistMetaServices.getReviewChecklistData('active', 'protocol_statistical_review', '');
      setProtocolChecklistMeta(data?.data || []);
    };

    loadChecklistMeta();
  }, []);

  useEffect(() => {
    const loadSavedReview = async () => {
      if (!projectId) {
        setProtocol(null);
        setExtractedText('');
        setMappedSections({});
        setFileName('');
        setResponses({});
        setScore({ applicable: 0, reviewed: 0, pct: 0, openCrit: 0, openMajor: 0, conclusion: 'Pending' });
        return;
      }

      const data = await studyProtocolReviewServices.getStudyProtocolReview('protocol_statistical_review', 'active', projectId);
      const row = data?.data?.[0];

      if (!row) {
        setProtocol(null);
        setExtractedText('');
        setMappedSections({});
        setFileName('');
        setResponses({});
        setScore({ applicable: 0, reviewed: 0, pct: 0, openCrit: 0, openMajor: 0, conclusion: 'Pending' });
        return;
      }

      setProtocol(row);
      setExtractedText(row.extractedText || '');
      setMappedSections(row.mappedSections || {});
      setFileName(row.fileName || '');
      setResponses(row.responses || {});
      setScore(row.score || { applicable: 0, reviewed: 0, pct: 0, openCrit: 0, openMajor: 0, conclusion: 'Pending' });
    };

    loadSavedReview();
  }, [projectId]);

  const handleFileUpload = async (file) => {
    try {
      const text = await file.text();
      setExtractedText(text);
      setFileName(file.name);
      toast('File uploaded successfully', 'ok');
      
      const sections = extractProtocolSections(text);
      setMappedSections(sections);
      
      audit('PROTOCOL_UPLOAD', file.name, 'Protocol uploaded for review', { projectId });

      if (projectId) {
        const payload = {
          id: protocol?._id,
          reviewId: protocol?.reviewId,
          reviewType: 'protocol_statistical_review',
          projectId,
          fileName: file.name,
          extractedText: text,
          mappedSections: sections,
          responses,
          score,
          updatedAt: new Date().toISOString()
        };

        const data = protocol?._id
          ? await studyProtocolReviewServices.updateStudyProtocolReview(payload)
          : await studyProtocolReviewServices.addNewStudyProtocolReview(payload);

        if (data?.data) {
          setProtocol(data.data);
        }
      }
    } catch (error) {
      toast('Error uploading file: ' + error.message, 'error');
    }
  };

  const extractProtocolSections = (text) => {
    const sections = {};
    const sectionMap = [
      { key: 'Study Objective', patterns: [/objective/i, /primary objective/i, /study objective/i] },
      { key: 'Study Design', patterns: [/study design/i, /design/i] },
      { key: 'Sample Size', patterns: [/sample size/i, /power/i, /sample size calculation/i] },
      { key: 'PK Parameters', patterns: [/pk parameters/i, /pharmacokinetic/i, /cmax/i, /auc/i] },
      { key: 'Statistical Analysis', patterns: [/statistical analysis/i, /statistical methods/i, /anova/i] },
      { key: 'BE Criteria', patterns: [/be criteria/i, /bioequivalence criteria/i, /acceptance limits/i] },
      { key: 'Analysis Populations', patterns: [/analysis population/i, /population/i, /completer/i] }
    ];

    const lines = text.split('\n');
    sectionMap.forEach(({ key, patterns }) => {
      let found = [];
      for (let i = 0; i < lines.length; i++) {
        if (patterns.some(p => p.test(lines[i]))) {
          let snippet = lines[i];
          for (let j = i + 1; j < Math.min(i + 15, lines.length); j++) {
            if (lines[j].trim() === '' || lines[j].match(/^[A-Z][a-z]+:/)) break;
            snippet += '\n' + lines[j];
          }
          found.push(snippet.trim());
          break;
        }
      }
      if (found.length > 0) {
        sections[key] = found.join('\n\n');
      }
    });
    return sections;
  };

  const saveProtocol = async () => {
    if (!projectId) {
      toast('Please select a project first', 'warn');
      return;
    }

    const items = getChecklistItems();
    let reviewed = 0;
    let openCrit = 0;
    let openMajor = 0;
    items.forEach((item) => {
      const r = responses[item.id];
      if (r?.compliance) {
        reviewed++;
        if (r.findingStatus === 'Open') {
          if (item.sev === 'Critical') openCrit++;
          if (item.sev === 'Major') openMajor++;
        }
      }
    });
    const applicable = items.length;
    const pct = applicable > 0 ? Math.round((reviewed / applicable) * 100) : 0;
    let conclusion = 'Pending review';
    if (openCrit > 0) conclusion = 'Critical revision required';
    else if (openMajor > 0) conclusion = 'Major revision required';
    else if (pct === 100 && reviewed === applicable) conclusion = 'Acceptable';
    else if (pct >= 90) conclusion = 'Acceptable with minor comments';
    else if (pct > 0) conclusion = 'Revision recommended';

    const liveScore = { applicable, reviewed, pct, openCrit, openMajor, conclusion };
    setScore(liveScore);

    const payload = {
      id: protocol?._id,
      reviewId: protocol?.reviewId,
      reviewType: 'protocol_statistical_review',
      projectId,
      fileName,
      extractedText,
      mappedSections,
      responses,
      score: liveScore,
      updatedAt: new Date().toISOString()
    };

    const data = protocol?._id
      ? await studyProtocolReviewServices.updateStudyProtocolReview(payload)
      : await studyProtocolReviewServices.addNewStudyProtocolReview(payload);

    if (data?.data) {
      setProtocol(data.data);
    }

    audit('PROTOCOL_SAVE', fileName, 'Protocol saved', { projectId });
    toast('Protocol saved successfully', 'ok');
  };

  const saveChecklist = async () => {
    await saveProtocol();
    audit('PROTOCOL_CHECKLIST_SAVE', fileName || 'Checklist', 'Protocol checklist saved', { projectId });
  };

  const saveReport = async () => {
    await saveProtocol();
    audit('PROTOCOL_REPORT_SAVE', fileName || 'Report', 'Protocol report saved', { projectId });
  };

  const updateResponse = (itemId, field, value) => {
    setResponses(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], [field]: value }
    }));
  };

  const clearChecklistFilters = () => {
    setChecklistFilter({ severity: 'all', compliance: 'all', status: 'all', search: '' });
  };

  const getFilteredChecklistItems = (items) => {
    const search = checklistFilter.search.trim().toLowerCase();

    return items.filter((item) => {
      const r = responses[item.id] || {};
      const severityOk = checklistFilter.severity === 'all' || item.sev === checklistFilter.severity;
      const complianceOk = checklistFilter.compliance === 'all' || (r.compliance || 'blank') === checklistFilter.compliance;
      const statusOk = checklistFilter.status === 'all' || (r.findingStatus || 'Open') === checklistFilter.status;
      const searchOk =
        !search ||
        String(item.id).toLowerCase().includes(search) ||
        item.cat.toLowerCase().includes(search) ||
        item.point.toLowerCase().includes(search);

      return severityOk && complianceOk && statusOk && searchOk;
    });
  };

  const escapeHtml = (value) => {
    const str = String(value || '');
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  const getReportContext = () => {
    const selectedProject = projects.find((p) => p.id === projectId);
    const selectedClient = clients.find((c) => c.id === selectedProject?.clientId);
    const items = getChecklistItems();
    const criticalMajorFindings = items.filter((item) => {
      const r = responses[item.id];
      return r?.compliance === 'No' && (item.sev === 'Critical' || item.sev === 'Major');
    });
    const recommendations = items.filter((item) => {
      const r = responses[item.id];
      return r?.compliance === 'No';
    }).slice(0, 5);

    return {
      selectedProject,
      selectedClient,
      criticalMajorFindings,
      recommendations
    };
  };

  const buildReportHtml = () => {
    const { selectedProject, selectedClient, criticalMajorFindings, recommendations } = getReportContext();
    const generatedAt = new Date().toLocaleString();

    const findingsRows = criticalMajorFindings.length > 0
      ? criticalMajorFindings.map((item, idx) => `
        <tr>
          <td>${idx + 1}</td>
          <td>${escapeHtml(item.cat)}</td>
          <td>${escapeHtml(item.point)}</td>
          <td>${escapeHtml(item.sev)}</td>
          <td>${escapeHtml(`Verify and address: ${item.point}`)}</td>
          <td>Open</td>
        </tr>
      `).join('')
      : '<tr><td colspan="6" style="text-align:center; padding:10px;">No Critical or Major findings</td></tr>';

    const recommendationRows = recommendations.length > 0
      ? recommendations.map((item) => `<li><strong>${escapeHtml(item.cat)}:</strong> ${escapeHtml(item.point)}</li>`).join('')
      : '<li>No recommendations - all checks passed</li>';

    return `
      <div>
        <h1 style="margin-bottom: 8px;">Protocol Statistical Review Report</h1>
        <div style="margin-bottom: 12px;"><strong>Project:</strong> ${escapeHtml(selectedProject?.name || 'Untitled Project')}</div>
        <div style="margin-bottom: 4px;"><strong>Protocol Number:</strong> ${escapeHtml(selectedProject?.protocolNumber || '-')}</div>
        <div style="margin-bottom: 4px;"><strong>Client:</strong> ${escapeHtml(selectedClient?.name || '-')}</div>
        <div style="margin-bottom: 4px;"><strong>Reviewer:</strong> ${escapeHtml(currentUser?.name || '-')}</div>
        <div style="margin-bottom: 16px;"><strong>Generated:</strong> ${escapeHtml(generatedAt)}</div>

        <h2>1. Executive Summary</h2>
        <p><strong>Conclusion:</strong> ${escapeHtml(score.conclusion)}</p>
        <p><strong>Compliance Score:</strong> ${escapeHtml(score.pct)}%</p>
        <p><strong>Open Critical:</strong> ${escapeHtml(score.openCrit)} | <strong>Open Major:</strong> ${escapeHtml(score.openMajor)}</p>

        <h2>2. Protocol Overview</h2>
        <h3>Study Objective</h3>
        <p>${escapeHtml(mappedSections['Study Objective'] || 'Not extracted')}</p>
        <h3>Study Design</h3>
        <p>${escapeHtml(mappedSections['Study Design'] || 'Not extracted')}</p>
        <h3>Statistical Analysis</h3>
        <p>${escapeHtml(mappedSections['Statistical Analysis'] || 'Not extracted')}</p>

        <h2>3. Critical & Major Findings</h2>
        <table border="1" cellspacing="0" cellpadding="6" style="border-collapse: collapse; width: 100%; font-size: 12px;">
          <thead>
            <tr>
              <th>#</th>
              <th>Category</th>
              <th>Issue Identified</th>
              <th>Severity</th>
              <th>Required Action</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${findingsRows}
          </tbody>
        </table>

        <h2>4. Recommendations</h2>
        <ol>
          ${recommendationRows}
        </ol>
      </div>
    `;
  };

  const exportReportPdf = () => {
    const reportHtml = buildReportHtml();
    const printWindow = window.open('', '_blank');

    if (!printWindow) {
      toast('Popup blocked. Please allow popups to export PDF.', 'warn');
      return;
    }

    printWindow.document.write(`
      <html>
        <head>
          <title>Protocol Review Report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; color: #111; line-height: 1.45; }
            h1, h2, h3 { margin: 0 0 8px; }
            h2 { margin-top: 20px; }
            p { margin: 0 0 8px; }
            table { margin-top: 8px; }
            @media print {
              body { padding: 0; }
            }
          </style>
        </head>
        <body>
          ${reportHtml}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    audit('PROTOCOL_REPORT_EXPORT_PDF', fileName || 'Protocol report', 'Protocol report exported as PDF', { projectId });
    toast('Report opened for PDF export', 'ok');
  };

  const exportReportDoc = () => {
    const reportHtml = buildReportHtml();
    const docContent = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta charset="utf-8" />
          <title>Protocol Review Report</title>
        </head>
        <body>${reportHtml}</body>
      </html>
    `;

    const blob = new Blob(['\ufeff', docContent], { type: 'application/msword;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const selectedProject = projects.find((p) => p.id === projectId);
    const safeName = (selectedProject?.name || 'protocol-review-report').replace(/[^a-z0-9-_]+/gi, '-').toLowerCase();
    const a = document.createElement('a');
    a.href = url;
    a.download = `${safeName}.doc`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    audit('PROTOCOL_REPORT_EXPORT_DOC', fileName || 'Protocol report', 'Protocol report exported as DOC', { projectId });
    toast('DOC export completed', 'ok');
  };

  const getChecklistItems = () => [
    ...(protocolChecklistMeta.length > 0
      ? protocolChecklistMeta.map((item, index) => ({
        id: item.reviewChecklistId || item._id || String(index + 1),
        cat: item.category || 'General',
        point: item.checkpoint || '',
        sev: item.severity || 'Major',
      }))
      : [
    { id: 1, cat: 'Regulatory pathway', point: 'Confirm regulatory pathway and applicable guidance are correctly cited.', sev: 'Critical' },
    { id: 2, cat: 'Study design', point: 'Confirm the study design is correctly stated and statistically appropriate.', sev: 'Critical' },
    { id: 3, cat: 'Design consistency', point: 'Confirm number of treatments, periods, sequences are identical throughout.', sev: 'Major' },
    { id: 4, cat: 'Test / reference ID', point: 'Confirm test and reference products are correctly identified.', sev: 'Major' },
    { id: 5, cat: 'Randomization', point: 'Confirm randomization method, sequence balance, and allocation ratio.', sev: 'Major' },
    { id: 6, cat: 'Washout', point: 'Confirm washout is adequate and justified from half-life.', sev: 'Major' },
    { id: 7, cat: 'Fasting / fed', point: 'Confirm fasting/fed condition matches regulatory requirement.', sev: 'Major' },
    { id: 8, cat: 'Objective / hypothesis', point: 'Confirm primary objective and BE hypothesis support valid BE conclusion.', sev: 'Critical' },
    { id: 9, cat: 'Primary PK parameters', point: 'Confirm primary PK parameters are correctly defined.', sev: 'Critical' },
    { id: 10, cat: 'Secondary PK parameters', point: 'Confirm secondary PK parameters are defined.', sev: 'Minor' },
    { id: 11, cat: 'Sampling schedule', point: 'Confirm sampling schedule supports reliable estimation of parameters.', sev: 'Major' },
    { id: 12, cat: 'Concentration handling', point: 'Confirm BLQ, pre-dose, missing-sample handling rules are pre-specified.', sev: 'Major' },
    { id: 13, cat: 'Sample size', point: 'Confirm all sample-size assumptions are stated and justified.', sev: 'Critical' },
    { id: 14, cat: 'Evaluable subjects', point: 'Confirm minimum evaluable number and final enrolled number are justified.', sev: 'Major' },
    { id: 15, cat: 'Analysis populations', point: 'Confirm analysis population definitions are pre-specified.', sev: 'Major' },
    { id: 16, cat: 'Dropouts / withdrawals', point: 'Confirm dropout and withdrawal handling rules are pre-specified.', sev: 'Major' },
    { id: 17, cat: 'Vomiting / diarrhea', point: 'Confirm the vomiting/diarrhea exclusion rule is appropriate.', sev: 'Minor' },
    { id: 18, cat: 'Outlier policy', point: 'Confirm outlier policy does not allow post-hoc exclusion to rescue failed BE.', sev: 'Critical' },
    { id: 19, cat: 'Log transformation', point: 'Confirm log transformation of primary PK parameters is specified.', sev: 'Critical' },
    { id: 20, cat: 'ANOVA / model effects', point: 'Confirm analysis model includes correct effects.', sev: 'Critical' },
    { id: 21, cat: 'Subject effect', point: 'Confirm subject effect is handled correctly for the design type.', sev: 'Major' },
    { id: 22, cat: 'TOST / 90% CI', point: 'Confirm TOST approach and 90% confidence interval are clearly described.', sev: 'Critical' },
    { id: 23, cat: 'GMR / back-transform', point: 'Confirm geometric LSMean ratio and back-transformation method.', sev: 'Major' },
    { id: 24, cat: 'BE acceptance limits', point: 'Confirm BE acceptance criteria (80.00–125.00%).', sev: 'Critical' },
    { id: 25, cat: 'Pairwise pre-specification', point: 'For 3-/4-way designs confirm all planned pairwise comparisons are pre-specified.', sev: 'Major' },
    { id: 26, cat: 'Independent conclusions', point: 'For 2 test + 1 reference confirm T1/R and T2/R conclusions are independent.', sev: 'Major' },
    { id: 27, cat: 'Comparison matrix', point: 'For 2T+2R or 3T+1R confirm comparison matrix and reference selection.', sev: 'Major' },
    { id: 28, cat: 'Multiplicity', point: 'Confirm multiplicity approach or justification is stated.', sev: 'Minor' },
    { id: 29, cat: 'CVwR estimability', point: 'For partial replicate confirm CVwR can be estimated.', sev: 'Critical' },
    { id: 30, cat: 'CVwT / interaction', point: 'For fully replicate confirm CVwT, CVwR, and interaction are estimable.', sev: 'Major' },
    { id: 31, cat: 'Scaled BE method', point: 'Confirm correct scaled-BE method is specified.', sev: 'Critical' },
    { id: 32, cat: 'Point-estimate constraint', point: 'Confirm point-estimate constraint is included for scaled BE.', sev: 'Critical' },
    { id: 33, cat: 'Scaled limits / cap', point: 'Confirm scaled BE limits, regulatory cap, and switching rule are described.', sev: 'Major' },
    { id: 34, cat: 'NTI handling', point: 'Confirm narrow-therapeutic-index handling where applicable.', sev: 'Major' },
    { id: 35, cat: 'Software / version', point: 'Confirm statistical software and version are stated.', sev: 'Minor' },
    { id: 36, cat: 'Planned outputs', point: 'Confirm planned outputs include all required statistical tables.', sev: 'Minor' }
  ])
  ];

  const computeScore = () => {
    const items = getChecklistItems();
    let reviewed = 0, applicable = items.length, openCrit = 0, openMajor = 0;
    items.forEach(item => {
      const r = responses[item.id];
      if (r?.compliance) {
        reviewed++;
        if (r.findingStatus === 'Open') {
          if (item.sev === 'Critical') openCrit++;
          if (item.sev === 'Major') openMajor++;
        }
      }
    });
    const pct = applicable > 0 ? Math.round((reviewed / applicable) * 100) : 0;
    let conclusion = 'Pending review';
    if (openCrit > 0) conclusion = 'Critical revision required';
    else if (openMajor > 0) conclusion = 'Major revision required';
    else if (pct === 100 && reviewed === applicable) conclusion = 'Acceptable';
    else if (pct >= 90) conclusion = 'Acceptable with minor comments';
    else if (pct > 0) conclusion = 'Revision recommended';
    
    setScore({ applicable, reviewed, pct, openCrit, openMajor, conclusion });
  };

  useEffect(() => {
    computeScore();
  }, [responses]);

  const renderUploadTab = () => (
    <div>
      <div className="card card-pad mb-4">
        <div className="card-title">
          <i className="ti ti-cloud-upload" /> Upload Protocol
        </div>
        <div className="field">
          <label className="field-label">Select Project</label>
          <select 
            className="inp" 
            value={projectId || ''} 
            onChange={(e) => setProjectId(e.target.value)}
          >
            <option value="">— Select a project —</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.name || p.code}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="field-label">Upload Protocol File</label>
          <input 
            type="file" 
            className="inp" 
            accept=".pdf,.docx,.txt,.html"
            onChange={(e) => {
              if (e.target.files[0]) handleFileUpload(e.target.files[0]);
            }}
          />
          <div className="field-hint">Supported: PDF, DOCX, TXT, HTML</div>
        </div>
        {fileName && (
          <div className="alert info" style={{ marginTop: 8, padding: 10, borderRadius: 6, background: 'var(--accent-soft)', border: '1px solid var(--accent-3)' }}>
            <i className="ti ti-file" /> {fileName} — {extractedText.length} characters extracted
          </div>
        )}
      </div>

      <div className="card card-pad">
        <div className="card-title">
          <i className="ti ti-file-text" /> Extracted Protocol Text
        </div>
        <textarea
          className="ta"
          value={extractedText}
          onChange={(e) => setExtractedText(e.target.value)}
            style={{ minHeight: 200, width: "100%", padding: 20 }}
          placeholder="Upload a file or paste protocol text here..."
        />
        <div className="tiny muted mt-2">{extractedText.length} characters</div>
        <div className="flex gap-2 mt-3">
          <button className="btn btn-primary" onClick={saveProtocol}>
            <i className="ti ti-save" /> Save Protocol
          </button>
          <button className="btn" onClick={() => {
            const sections = extractProtocolSections(extractedText);
            setMappedSections(sections);
            toast('Sections extracted', 'ok');
          }}>
            <i className="ti ti-refresh" /> Extract Sections
          </button>
        </div>
      </div>

      <div className="card card-pad mt-4">
        <div className="card-title">
          <i className="ti ti-folder" /> Mapped Sections
        </div>
        <div className="grid-2">
          {Object.entries(mappedSections).map(([key, value]) => (
            <div key={key} className="field">
              <label className="field-label">{key}</label>
              <textarea 
                className="ta" 
                value={value} 
                onChange={(e) => setMappedSections({ ...mappedSections, [key]: e.target.value })}
                style={{ minHeight: 60 }}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderChecklistTab = () => {
    const items = getChecklistItems();
    const filteredItems = getFilteredChecklistItems(items);

    return (
      <div>
        <div className="flex between mb-3">
          <div className="card-title">
            <i className="ti ti-checklist" /> Protocol Checklist
            <span className="tiny muted" style={{ marginLeft: 8 }}>{filteredItems.length} of {items.length} items</span>
          </div>
          <div className="flex gap-2">
            <button className="btn btn-sm btn-ghost" onClick={() => setShowFilters((v) => !v)}>
              <i className="ti ti-filter" /> Filter
            </button>
            <button className="btn btn-sm btn-primary" onClick={() => {
              items.forEach(item => {
                updateResponse(item.id, 'compliance', 'Yes');
                updateResponse(item.id, 'findingStatus', 'Resolved');
              });
              toast('All items marked as compliant', 'ok');
            }}>
              <i className="ti ti-check" /> Validate All
            </button>
            <button className="btn btn-sm btn-primary" onClick={saveChecklist}>
              <i className="ti ti-device-floppy" /> Save Checklist
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="card card-pad mb-3">
            <div className="grid-2" style={{ gap: 12 }}>
              <div className="field">
                <label className="field-label">Search</label>
                <input
                  className="inp"
                  value={checklistFilter.search}
                  onChange={(e) => setChecklistFilter((prev) => ({ ...prev, search: e.target.value }))}
                  placeholder="Category or checkpoint"
                />
              </div>
              <div className="field">
                <label className="field-label">Severity</label>
                <select
                  className="inp"
                  value={checklistFilter.severity}
                  onChange={(e) => setChecklistFilter((prev) => ({ ...prev, severity: e.target.value }))}
                >
                  <option value="all">All</option>
                  <option value="Critical">Critical</option>
                  <option value="Major">Major</option>
                  <option value="Minor">Minor</option>
                </select>
              </div>
              <div className="field">
                <label className="field-label">Compliance</label>
                <select
                  className="inp"
                  value={checklistFilter.compliance}
                  onChange={(e) => setChecklistFilter((prev) => ({ ...prev, compliance: e.target.value }))}
                >
                  <option value="all">All</option>
                  <option value="blank">Not Reviewed</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                  <option value="Partial">Partial</option>
                  <option value="Not Applicable">N/A</option>
                </select>
              </div>
              <div className="field">
                <label className="field-label">Status</label>
                <select
                  className="inp"
                  value={checklistFilter.status}
                  onChange={(e) => setChecklistFilter((prev) => ({ ...prev, status: e.target.value }))}
                >
                  <option value="all">All</option>
                  <option value="Open">Open</option>
                  <option value="Resolved">Resolved</option>
                  <option value="Deferred">Deferred</option>
                  <option value="Not Applicable">N/A</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2" style={{ marginTop: 8 }}>
              <button className="btn btn-sm btn-ghost" onClick={clearChecklistFilters}>
                <i className="ti ti-eraser" /> Clear
              </button>
            </div>
          </div>
        )}

        <div className="score-banner" style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 12,
          background: 'var(--surface-2)',
          padding: 16,
          borderRadius: 8,
          marginBottom: 16
        }}>
          <div>
            <div className="tiny muted">Applicable</div>
            <div className="metric-value" style={{ fontSize: 24 }}>{score.applicable}</div>
          </div>
          <div>
            <div className="tiny muted">Reviewed</div>
            <div className="metric-value" style={{ fontSize: 24, color: 'var(--ok)' }}>{score.reviewed}</div>
          </div>
          <div>
            <div className="tiny muted">Compliance</div>
            <div className="metric-value" style={{ fontSize: 24 }}>{score.pct}%</div>
          </div>
          <div>
            <div className="tiny muted">Open Critical</div>
            <div className="metric-value" style={{ fontSize: 24, color: 'var(--danger)' }}>{score.openCrit}</div>
          </div>
          <div>
            <div className="tiny muted">Open Major</div>
            <div className="metric-value" style={{ fontSize: 24, color: 'var(--warn)' }}>{score.openMajor}</div>
          </div>
          <div>
            <div className="tiny muted">Conclusion</div>
            <div className="metric-value" style={{ fontSize: 16 }}>{score.conclusion}</div>
          </div>
        </div>

        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th>Category</th>
                <th>Checkpoint</th>
                <th style={{ width: 100 }}>Severity</th>
                <th style={{ width: 120 }}>Compliance</th>
                <th style={{ width: 120 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => {
                const r = responses[item.id] || {};
                return (
                  <tr key={item.id} className={item.sev === 'Critical' ? 'row-crit' : item.sev === 'Major' ? 'row-major' : ''}>
                    <td className="num">{item.id}</td>
                    <td>{item.cat}</td>
                    <td>{item.point}</td>
                    <td><span className={`badge ${item.sev === 'Critical' ? 'crit' : item.sev === 'Major' ? 'major' : 'minor'}`}>{item.sev}</span></td>
                    <td>
                      <select 
                        className="btn btn-xs btn-ghost"
                        value={r.compliance || ''}
                        onChange={(e) => updateResponse(item.id, 'compliance', e.target.value)}
                      >
                        <option value="">—</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                        <option value="Partial">Partial</option>
                        <option value="Not Applicable">N/A</option>
                      </select>
                    </td>
                    <td>
                      <select 
                        className="btn btn-xs btn-ghost"
                        value={r.findingStatus || 'Open'}
                        onChange={(e) => updateResponse(item.id, 'findingStatus', e.target.value)}
                      >
                        <option value="Open">Open</option>
                        <option value="Resolved">Resolved</option>
                        <option value="Deferred">Deferred</option>
                        <option value="Not Applicable">N/A</option>
                      </select>
                    </td>
                  </tr>
                );
              })}
              {filteredItems.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                    No checklist item matches selected filters
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ==================== CSR-STYLE REPORT ====================
  const renderReportTab = () => (
    <div>
      <div className="flex between mb-3">
        <div className="card-title">
          <i className="ti ti-file-text" /> Protocol Review Report
        </div>
        <div className="flex gap-2">
          <button className="btn btn-sm btn-ghost" onClick={exportReportPdf}>
            <i className="ti ti-download" /> Export PDF
          </button>
          <button className="btn btn-sm btn-ghost" onClick={exportReportDoc}>
            <i className="ti ti-file-type-doc" /> Export DOC
          </button>
          <button className="btn btn-sm btn-primary" onClick={saveReport}>
            <i className="ti ti-save" /> Save Report
          </button>
        </div>
      </div>

      <div className="report-paper draft card-pad">
        <div className="cover">
          <div className="deck">Protocol Statistical Review Report</div>
          <h1>{projects.find(p => p.id === projectId)?.name || 'Untitled Project'}</h1>
          <div className="muted" style={{ marginTop: 8 }}>Protocol Number: {projects.find(p => p.id === projectId)?.protocolNumber || '—'}</div>
          <div className="meta-grid" style={{ marginTop: 24 }}>
            <div><div className="k">Client</div><div className="v">{clients.find(c => c.id === projects.find(p => p.id === projectId)?.clientId)?.name || '—'}</div></div>
            <div><div className="k">Reviewer</div><div className="v">{currentUser?.name || '—'}</div></div>
            <div><div className="k">Report Generated</div><div className="v">{new Date().toLocaleString()}</div></div>
            <div><div className="k">Compliance Score</div><div className="v">{score.pct}%</div></div>
          </div>
        </div>

        <h2>1. Executive Summary</h2>
        <div className={`verdict ${score.openCrit > 0 ? 'crit' : score.openMajor > 0 ? 'major' : 'ok'}`}>
          <div className="label">Overall Conclusion</div>
          <div className="text">{score.conclusion}</div>
        </div>
        <div className="meta-grid">
          <div><div className="k">Total Items</div><div className="v">{score.applicable}</div></div>
          <div><div className="k">Reviewed</div><div className="v">{score.reviewed}</div></div>
          <div><div className="k">Compliance Score</div><div className="v">{score.pct}%</div></div>
          <div><div className="k">Open Critical</div><div className="v" style={{ color: 'var(--danger)' }}>{score.openCrit}</div></div>
          <div><div className="k">Open Major</div><div className="v" style={{ color: 'var(--warn)' }}>{score.openMajor}</div></div>
        </div>

        <h2>2. Protocol Overview</h2>
        <h3>Study Objective</h3>
        <p>{mappedSections['Study Objective'] || 'Not extracted'}</p>
        <h3>Study Design</h3>
        <p>{mappedSections['Study Design'] || 'Not extracted'}</p>
        <h3>Statistical Analysis</h3>
        <p>{mappedSections['Statistical Analysis'] || 'Not extracted'}</p>

        <h2>3. Critical &amp; Major Findings</h2>
        <table className="tbl-findings">
          <thead>
            <tr>
              <th>#</th>
              <th>Category</th>
              <th>Issue Identified</th>
              <th>Severity</th>
              <th>Required Action</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {getChecklistItems().filter(item => {
              const r = responses[item.id];
              return r?.compliance === 'No' && (item.sev === 'Critical' || item.sev === 'Major');
            }).map((item, idx) => (
              <tr key={item.id}>
                <td>{idx + 1}</td>
                <td>{item.cat}</td>
                <td>{item.point}</td>
                <td><span className={`badge ${item.sev === 'Critical' ? 'crit' : 'major'}`}>{item.sev}</span></td>
                <td>Verify and address: {item.point}</td>
                <td><span className="badge crit">Open</span></td>
              </tr>
            ))}
            {getChecklistItems().filter(item => {
              const r = responses[item.id];
              return r?.compliance === 'No' && (item.sev === 'Critical' || item.sev === 'Major');
            }).length === 0 && (
              <tr>
                <td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                  No Critical or Major findings
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <h2>4. Recommendations</h2>
        <ol style={{ marginLeft: 22, fontSize: 13 }}>
          {getChecklistItems().filter(item => {
            const r = responses[item.id];
            return r?.compliance === 'No';
          }).slice(0, 5).map((item, idx) => (
            <li key={item.id}><strong>{item.cat}:</strong> {item.point}</li>
          ))}
          {getChecklistItems().filter(item => {
            const r = responses[item.id];
            return r?.compliance === 'No';
          }).length === 0 && (
            <li>No recommendations — all checks passed</li>
          )}
        </ol>

        <div className="sig-block">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32, marginTop: 24 }}>
            <div>
              <div className="k">Reviewer</div>
              <div style={{ height: 48, borderBottom: '1px solid var(--ink)', margin: '8px 0' }}></div>
              <div>{currentUser?.name || '—'}<br /><span className="tiny muted">Date: {new Date().toLocaleDateString()}</span></div>
            </div>
            <div>
              <div className="k">Approved by</div>
              <div style={{ height: 48, borderBottom: '1px solid var(--ink)', margin: '8px 0' }}></div>
              <div>—<br /><span className="tiny muted">Signature &amp; Date</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="protocol-review-module">
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Study Document Review</div>
          <h1 className="page-title">Protocol Statistical Review</h1>
          <p className="page-sub">Upload, review, and generate reports for clinical study protocols.</p>
        </div>
        <div className="flex gap-2">
          <span className="tag tag-ok tag-dot">Validated</span>
          <span className="tag tag-info">ICH E9 / M13A</span>
        </div>
      </div>

      <div className="tabs" style={{ 
        display: 'flex', 
        gap: 4, 
        borderBottom: '1px solid var(--line)',
        marginBottom: 24,
      }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '10px 16px',
              background: 'transparent',
              border: 'none',
              fontFamily: 'inherit',
              fontSize: 13,
              fontWeight: 500,
              color: activeTab === tab.id ? 'var(--ink)' : 'var(--ink-3)',
              cursor: 'pointer',
              borderBottom: activeTab === tab.id ? '2px solid var(--accent-3)' : '2px solid transparent',
              marginBottom: -1,
              whiteSpace: 'nowrap'
            }}
          >
            <i className={`ti ${tab.icon}`} style={{ marginRight: 6 }} />
            {tab.label}
          </button>
        ))}
      </div>

      <div className="tab-content">
        {activeTab === 'upload' && renderUploadTab()}
        {activeTab === 'checklist' && renderChecklistTab()}
        {activeTab === 'report' && renderReportTab()}
      </div>

      <style>{`
        .protocol-review-module .row-crit td {
          background: var(--danger-soft);
        }
        .protocol-review-module .row-major td {
          background: var(--warn-soft);
        }
        .protocol-review-module .badge {
          display: inline-block;
          padding: 2px 8px;
          font-size: 10px;
          font-weight: 600;
          border-radius: 2px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border: 1px solid currentColor;
        }
        .protocol-review-module .badge.crit {
          color: var(--danger);
          background: var(--danger-soft);
        }
        .protocol-review-module .badge.major {
          color: var(--warn);
          background: var(--warn-soft);
        }
        .protocol-review-module .badge.minor {
          color: var(--info);
          background: var(--info-soft);
        }
        .protocol-review-module .badge.ok {
          color: var(--ok);
          background: var(--ok-soft);
        }
        .protocol-review-module .badge.draft {
          color: var(--ink-3);
          background: var(--surface-2);
        }
        .protocol-review-module .alert.info {
          background: var(--info-soft);
          border: 1px solid var(--info);
          color: var(--info);
          padding: 12px;
          border-radius: 6px;
        }
        .protocol-review-module .report-paper {
          background: #ffffff;
          border: 1px solid var(--line);
          padding: 32px 40px;
          font-size: 13px;
          line-height: 1.65;
          color: var(--ink);
          position: relative;
          border-radius: 8px;
        }
        .protocol-review-module .report-paper.draft::before {
          content: 'DRAFT';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%) rotate(-32deg);
          font-size: 100px;
          font-weight: 700;
          color: rgba(0,0,0,0.03);
          pointer-events: none;
          letter-spacing: -0.04em;
        }
        .protocol-review-module .report-paper .cover {
          text-align: center;
          padding: 0 0 24px;
          border-bottom: 2px solid var(--ink);
          margin-bottom: 24px;
        }
        .protocol-review-module .report-paper .deck {
          font-style: italic;
          color: var(--accent-3);
          text-transform: uppercase;
          letter-spacing: 0.1em;
          font-size: 12px;
          margin-bottom: 12px;
        }
        .protocol-review-module .report-paper .verdict {
          padding: 14px;
          border: 2px solid var(--ink);
          background: var(--surface-2);
          margin: 14px 0;
          text-align: center;
        }
        .protocol-review-module .report-paper .verdict.crit {
          border-color: var(--danger);
          background: var(--danger-soft);
        }
        .protocol-review-module .report-paper .verdict.major {
          border-color: var(--warn);
          background: var(--warn-soft);
        }
        .protocol-review-module .report-paper .verdict.ok {
          border-color: var(--ok);
          background: var(--ok-soft);
        }
        .protocol-review-module .report-paper .verdict .label {
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: var(--ink-3);
        }
        .protocol-review-module .report-paper .verdict .text {
          font-size: 18px;
          font-weight: 600;
          margin-top: 4px;
        }
        .protocol-review-module .report-paper .meta-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px 24px;
          margin: 18px 0;
          font-size: 13px;
        }
        .protocol-review-module .report-paper .meta-grid .k {
          color: var(--ink-3);
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }
        .protocol-review-module .report-paper .meta-grid .v {
          font-weight: 500;
        }
        .protocol-review-module .report-paper .sig-block {
          margin-top: 36px;
          padding-top: 14px;
          border-top: 1px solid var(--line);
          font-size: 12px;
        }
        .protocol-review-module .report-paper .sig-block .k {
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--ink-3);
        }
        .protocol-review-module .score-banner {
          background: var(--surface-2);
          padding: 16px;
          border-radius: 8px;
          margin-bottom: 16px;
        }
        .protocol-review-module .score-banner .metric-value {
          font-family: var(--display);
          font-size: 24px;
          font-weight: 500;
          line-height: 1.1;
        }
        .protocol-review-module .grid-2 {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px;
        }
        @media (max-width: 768px) {
          .protocol-review-module .grid-2 {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}

export default ProtocolReviewModule;